import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { dashboardAPI } from '../services/api';
import { 
  TrendingUp, TrendingDown, CreditCard, Wallet, 
  ArrowUpRight, RefreshCw, Clock, CheckCircle, XCircle 
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const DashboardPage = () => {
  const { merchant } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentPayments, setRecentPayments] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('week');

  useEffect(() => {
    fetchData();
  }, [period]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [statsRes, paymentsRes, chartRes] = await Promise.all([
        dashboardAPI.stats(),
        dashboardAPI.recentPayments(),
        dashboardAPI.chartData(period)
      ]);
      setStats(statsRes.data);
      setRecentPayments(paymentsRes.data.payments || []);
      setChartData(chartRes.data.data || []);
    } catch (err) {
      console.error('Dashboard error:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatAmount = (amount, currency = 'KZT') => {
    if (currency === 'KZT') return `${(amount || 0).toLocaleString('ru-RU')} ₸`;
    return `${((amount || 0) / 100).toFixed(2)} ${currency}`;
  };

  const StatCard = ({ title, value, subValue, icon: Icon, trend, color }) => (
    <div className="card p-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-slate-400 text-sm">{title}</p>
          <p className="text-2xl font-bold text-white mt-1">{value}</p>
          {subValue && <p className="text-slate-400 text-sm mt-1">{subValue}</p>}
        </div>
        <div className={`p-3 rounded-lg ${color}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
      {trend !== undefined && (
        <div className={`flex items-center gap-1 mt-4 text-sm ${trend >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
          {trend >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
          <span>{Math.abs(trend)}% vs прошлый период</span>
        </div>
      )}
    </div>
  );

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved': return <CheckCircle className="w-4 h-4 text-emerald-400" />;
      case 'declined': case 'failed': return <XCircle className="w-4 h-4 text-red-400" />;
      default: return <Clock className="w-4 h-4 text-amber-400" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'approved': return 'Успешно';
      case 'declined': return 'Отклонён';
      case 'failed': return 'Ошибка';
      case 'pending': return 'В обработке';
      default: return status;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-emerald-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Дашборд</h1>
          <p className="text-slate-400 mt-1">Добро пожаловать, {merchant?.company_name}</p>
        </div>
        <button onClick={fetchData} className="btn-secondary flex items-center gap-2">
          <RefreshCw className="w-4 h-4" />
          Обновить
        </button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Баланс к выплате"
          value={formatAmount(stats?.balance)}
          icon={Wallet}
          color="bg-emerald-500"
        />
        <StatCard
          title="Оборот сегодня"
          value={formatAmount(stats?.today_turnover)}
          subValue={`${stats?.today_count || 0} платежей`}
          icon={CreditCard}
          color="bg-blue-500"
          trend={stats?.today_trend}
        />
        <StatCard
          title="Конверсия"
          value={`${(stats?.conversion || 0).toFixed(1)}%`}
          subValue="Успешные / Всего"
          icon={TrendingUp}
          color="bg-purple-500"
        />
        <StatCard
          title="Оборот за месяц"
          value={formatAmount(stats?.month_turnover)}
          subValue={`${stats?.month_count || 0} платежей`}
          icon={CreditCard}
          color="bg-amber-500"
          trend={stats?.month_trend}
        />
      </div>

      {/* Chart */}
      <div className="card p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-white">Динамика платежей</h3>
          <div className="flex gap-2">
            {['week', 'month', 'year'].map(p => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={`px-3 py-1 rounded-lg text-sm ${period === p ? 'bg-emerald-500 text-white' : 'bg-slate-700 text-slate-400 hover:bg-slate-600'}`}
              >
                {p === 'week' ? 'Неделя' : p === 'month' ? 'Месяц' : 'Год'}
              </button>
            ))}
          </div>
        </div>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey="date" stroke="#94a3b8" fontSize={12} />
              <YAxis stroke="#94a3b8" fontSize={12} />
              <Tooltip
                contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                labelStyle={{ color: '#fff' }}
              />
              <Line type="monotone" dataKey="amount" stroke="#22c55e" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Recent Payments */}
      <div className="card">
        <div className="p-6 border-b border-slate-700/50 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-white">Последние платежи</h3>
          <Link to="/payments" className="text-emerald-400 hover:text-emerald-300 text-sm flex items-center gap-1">
            Все платежи <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-700/50">
                <th className="text-left p-4 text-sm font-medium text-slate-400">ID</th>
                <th className="text-left p-4 text-sm font-medium text-slate-400">Сумма</th>
                <th className="text-left p-4 text-sm font-medium text-slate-400">Статус</th>
                <th className="text-left p-4 text-sm font-medium text-slate-400">Дата</th>
              </tr>
            </thead>
            <tbody>
              {recentPayments.slice(0, 5).map(payment => (
                <tr key={payment.id} className="border-b border-slate-700/30 hover:bg-slate-700/20">
                  <td className="p-4 text-white font-mono text-sm">#{payment.id}</td>
                  <td className="p-4 text-white">{formatAmount(payment.amount, payment.currency)}</td>
                  <td className="p-4">
                    <span className="flex items-center gap-2">
                      {getStatusIcon(payment.status)}
                      <span className="text-slate-300">{getStatusText(payment.status)}</span>
                    </span>
                  </td>
                  <td className="p-4 text-slate-400 text-sm">
                    {new Date(payment.created_at).toLocaleString('ru-RU')}
                  </td>
                </tr>
              ))}
              {recentPayments.length === 0 && (
                <tr>
                  <td colSpan={4} className="p-8 text-center text-slate-400">
                    Платежей пока нет
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
